import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Brain, BarChart2, BookOpen,
  AlertCircle, Award, Target, Loader2, ArrowLeft, ChevronDown, ChevronUp,
  Star, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import api from '../../api';

const pctColor = p =>
  p >= 75 ? '#10b981' : p >= 60 ? '#3b82f6' : p >= 45 ? '#f59e0b' : '#ef4444';

const trendIcon = t =>
  t === 'up' || t === 'improving' ? <TrendingUp className="w-4 h-4 text-emerald-500" />
  : t === 'down' || t === 'declining' ? <TrendingDown className="w-4 h-4 text-rose-500" />
  : <Minus className="w-4 h-4 text-slate-400" />;

// ── Child Selector ────────────────────────────────────────────────────────────
function ChildCard({ child, selected, onClick }) {
  return (
    <button onClick={onClick} data-testid={`child-card-${child.id}`}
      className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all text-left w-full ${
        selected
          ? 'border-[#2563eb] bg-blue-50 shadow-sm'
          : 'border-slate-200 bg-white hover:border-blue-300'
      }`}>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
        {child.first_name?.[0] || '?'}
      </div>
      <div className="min-w-0">
        <p className="font-bold text-sm text-slate-800 truncate">
          {child.first_name} {child.last_name}
        </p>
        <p className="text-xs text-slate-400 truncate">{child.class_name} • {child.school_name}</p>
      </div>
    </button>
  );
}

// ── Growth Panel ──────────────────────────────────────────────────────────────
function GrowthPanel({ studentId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview | subjects

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/growth/parent/child/${studentId}`);
      if (res.success) setData(res.data);
    } catch { toast.error('Failed to load growth data'); }
    finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-7 h-7 text-[#2563eb] animate-spin" />
    </div>
  );

  if (!data || data.total_quiz_attempts === 0) return (
    <div className="text-center py-10">
      <BarChart2 className="w-10 h-10 text-slate-200 mx-auto mb-2" />
      <p className="text-sm text-slate-400">No quiz data yet for this child</p>
    </div>
  );

  const chartData = (data.subject_stats || []).slice(0, 6).map(s => ({
    subject: s.subject.length > 10 ? s.subject.slice(0, 10) + '…' : s.subject,
    avg: s.avg_pct,
  }));

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Overall Avg', value: `${data.overall_avg}%`, color: 'text-[#2563eb]' },
          { label: 'Best Subject', value: data.strongest_subject?.split(' ')[0] || '—', color: 'text-emerald-600' },
          { label: 'Needs Work',  value: data.weakest_subject?.split(' ')[0] || '—', color: 'text-rose-500' },
          { label: 'Sessions',    value: data.total_quiz_attempts, color: 'text-slate-700' },
        ].map(s => (
          <div key={s.label} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
            <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Trend */}
      <div className={`rounded-xl p-3.5 border flex items-center gap-2.5 ${
        data.trend === 'improving' ? 'bg-emerald-50 border-emerald-200' :
        data.trend === 'declining' ? 'bg-rose-50 border-rose-200' :
        'bg-slate-50 border-slate-200'
      }`}>
        {trendIcon(data.trend)}
        <div>
          <p className="font-bold text-sm text-slate-800">
            {data.trend === 'improving' ? 'Showing improvement!' :
             data.trend === 'declining' ? 'Performance dipping — may need support' :
             'Consistent performance'}
          </p>
          <p className="text-xs text-slate-500">Recent avg: {data.recent_avg}%</p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Subject Performance</p>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={chartData} margin={{ left: -20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="subject" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip formatter={v => `${v}%`} />
              <Bar dataKey="avg" name="Avg" radius={[3,3,0,0]}>
                {chartData.map((s, i) => <Cell key={i} fill={pctColor(s.avg)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Weak topics */}
      {data.weak_topics?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="font-bold text-amber-800 text-sm mb-2 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4" /> Topics Needing Practice
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.weak_topics.map((t, i) => (
              <span key={i} className="px-2.5 py-1 bg-amber-100 border border-amber-300 text-amber-700 rounded-full text-xs font-semibold">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* AI Plan if exists */}
      {data.ai_plan && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="font-bold text-blue-800 text-sm mb-2 flex items-center gap-1.5">
            <Brain className="w-4 h-4" /> AI Study Plan
          </p>
          <p className="text-sm text-blue-700">{data.ai_plan.summary}</p>
          {data.ai_plan.focus_areas?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {data.ai_plan.focus_areas.map((a, i) => (
                <span key={i} className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{a}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Prep Panel ────────────────────────────────────────────────────────────────
function PrepPanel({ studentId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/exam-prep/parent/child/${studentId}`);
      if (res.success) setData(res.data);
    } catch { toast.error('Failed to load prep data'); }
    finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-7 h-7 text-[#2563eb] animate-spin" />
    </div>
  );

  const sessions = data?.sessions || [];
  const summary  = data?.summary || {};

  if (sessions.length === 0) return (
    <div className="text-center py-10">
      <Brain className="w-10 h-10 text-slate-200 mx-auto mb-2" />
      <p className="text-sm text-slate-400">No AI exam prep sessions yet</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-black text-[#2563eb]">{summary.completed}</div>
          <div className="text-xs text-slate-500 mt-0.5">Completed Sessions</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-black text-emerald-600">{summary.avg_score}%</div>
          <div className="text-xs text-slate-500 mt-0.5">Average Score</div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="space-y-2.5">
        {sessions.map(s => (
          <div key={s.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0 ${
              s.status !== 'completed' ? 'bg-slate-100 text-slate-400' :
              (s.score_pct || 0) >= 75 ? 'bg-emerald-100 text-emerald-700' :
              (s.score_pct || 0) >= 50 ? 'bg-blue-100 text-blue-700' : 'bg-rose-100 text-rose-600'
            }`}>
              {s.status === 'completed' ? `${s.score_pct || 0}%` : '…'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-slate-800 truncate">{s.subject}</p>
              <p className="text-xs text-slate-400 truncate mt-0.5">
                {(s.topics || []).slice(0, 2).join(', ')} • <span className="capitalize">{s.difficulty}</span>
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-xs text-slate-400">{(s.generated_at || '').slice(0, 10)}</div>
              {s.weak_topics?.length > 0 && (
                <div className="text-xs text-amber-600 mt-0.5">{s.weak_topics.length} weak topics</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ChildProgressPage() {
  const navigate = useNavigate();
  const [children,      setChildren]     = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading,       setLoading]      = useState(true);
  const [activeTab,     setActiveTab]    = useState('growth'); // growth | prep

  const loadChildren = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/exams/parent/children');
      if (res.success && res.data.length > 0) {
        setChildren(res.data);
        setSelectedChild(res.data[0].id);
      }
    } catch { toast.error('Failed to load children'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadChildren(); }, [loadChildren]);

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <Loader2 className="w-10 h-10 text-[#2563eb] animate-spin" />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="child-progress-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/parent/dashboard')}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-md">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-black text-slate-800">Child Progress</h1>
          <p className="text-xs text-slate-500">Academic growth & AI exam prep activity</p>
        </div>
      </div>

      {/* Child Selector */}
      {children.length > 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-5">
          {children.map(c => (
            <ChildCard key={c.id} child={c} selected={selectedChild === c.id}
              onClick={() => setSelectedChild(c.id)} />
          ))}
        </div>
      )}

      {children.length === 0 ? (
        <div className="text-center py-16">
          <AlertCircle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">No children linked to your account</p>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 mb-5">
            {[
              { key: 'growth', label: 'Academic Growth' },
              { key: 'prep',   label: 'Exam Prep' },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                data-testid={`child-progress-tab-${t.key}`}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === t.key ? 'bg-white shadow-sm text-[#2563eb]' : 'text-slate-500 hover:text-slate-700'
                }`}>{t.label}</button>
            ))}
          </div>

          {selectedChild && (
            <div key={`${selectedChild}-${activeTab}`}>
              {activeTab === 'growth' && <GrowthPanel studentId={selectedChild} />}
              {activeTab === 'prep'   && <PrepPanel   studentId={selectedChild} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
